import React, { useState, useRef, useEffect, useContext, forwardRef, useImperativeHandle } from 'react';
import {
  Stack,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Box,
  Input,
} from '@chakra-ui/react';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';

const DrillDownContext = React.createContext();

export const DrillDown = forwardRef(({ children, getDrillDownItems }, ref) => {
  const [focusIndex, setFocusIndex] = useState(0);
  const [filterText, setFilterText] = useState(null);
  const listRef = useRef(null);
  useEffect(() => {
    setFocusIndex(0);
  }, [children]);
  const moveFocus = (amount) => {
    listRef.current?.moveFocus(amount);
  };
  const clickFocusedItem = () => {
    listRef.current?.clickFocusedItem();
  };
  useImperativeHandle(ref, () => ({
    moveFocus, clickFocusedItem
  }));

  return (
    <DrillDownContext.Provider value={{ setFilterText, filterText, focusIndex, setFocusIndex, getDrillDownItems }}>
        {React.Children.map(children, (child) => {
          if (child.type === DrillDownList) {
            return React.cloneElement(child, { ref: listRef });
          }
          return child;
        })}
    </DrillDownContext.Provider>
  );
});

export const DrillDownInput = (props) => {
  const { setFilterText } = useContext(DrillDownContext);
  return (
    <Input onChange={(e) => { setFilterText(e.target.value) }} {...props} />
  );
};

export const DrillDownList = forwardRef(({ children }, ref) => {
  const { filterText } = useContext(DrillDownContext);
  const [focusIndex, setFocusIndex] = useState(0);
  useEffect(() => {
    setFocusIndex(0);
  }, [children, filterText]);
  const childrenArray = React.Children.toArray(children);
  const moveFocus = (amount) => {
    let newIdx = (focusIndex + amount) % React.Children.count(children);
    if (newIdx < 0) {
      newIdx += childrenArray.length;
    }
    const step = amount < 0 ? -1 : 1;
    let child;
    for (let i=newIdx; i < childrenArray.length && 0 <= i; i += step) {
      child = childrenArray[newIdx];
      if (!child.props.value.match(filterText)) {
        setFocusIndex(i);
        return;
      }
    }
    setFocusIndex(0);
  };
  const clickFocusedItem = () => {
    const fn = childrenArray[focusIndex]?.props.onClick;
    fn && fn();
  };
  useImperativeHandle(ref, () => ({
    moveFocus, clickFocusedItem
  }));
  return (
    <Stack spacing={0} py={1} bg="bg" borderRadius={2}>
      {React.Children.toArray(children).filter(child => {
        return !filterText || child.props.value.match(filterText);
      }).map((child, i) => {
        return React.cloneElement(child, {
          onMouseEnter: () => setFocusIndex(i),
          isFocus: focusIndex === i,
        })
      })}
    </Stack>
  );
});

export const DrillDownItem = ({ value, drillDownValue, canDrillDown, children, isFocus, onClick, ...props }) => {
  const [childItems, setChildItems] = useState(null);
  const [isDrillDownInProgress, setIsDrillDownInProgress] = useState(false);
  const { getDrillDownItems } = useContext(DrillDownContext);
  const extraProps = isFocus ? { 'data-focus': true } : {};
  const onDrillDown = () => {
    if (!isDrillDownInProgress) {
      try {
        setIsDrillDownInProgress(true);
        getDrillDownItems(drillDownValue || value, value)
          .then(setChildItems);
      }
      finally {
        setTimeout(() => {
          setIsDrillDownInProgress(false);
        }, 1000);
      }
    }
  };
  return (
    <Popover trigger="hover" placement="right-start" gutter={0}>
    <Button
      value={value} variant="menu" pos="relative"
      onClick={onClick}
      {...extraProps}
      {...props}
    >
      {children}
      <PopoverTrigger>
        <Box pos="absolute" right={0} display={canDrillDown ? '' : 'none'}
          onMouseEnter={onDrillDown}
          px={4} py={2} mr={-2}>
          <FA icon="fa-caret-right" />
        </Box>
      </PopoverTrigger>
    </Button>
      <PopoverContent size="md">
        {childItems && (
          <DrillDown getDrillDownItems={getDrillDownItems}>
            {childItems}
          </DrillDown>
        )}
      </PopoverContent>
    </Popover>
  );
};
